import { AssignmentTurnedInOutlined, CheckCircleOutlined, DeleteRounded, VisibilityOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  DateRangePicker,
  LocalizationProvider,
  type DateRange,
  type PickersShortcutsItem,
} from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  ActionItemLabel,
  SmallInputLabel,
  SnackbarContent,
  TaskItemLabel,
  TextViewer,
  UserAutocomplete,
} from "src/components";
import { FormRecipientType, FormSubmissionPrivacyType, FormType } from "src/constants";
import { useSession } from "src/hooks";
import {
  FormQuestionDto,
  FormSubmissionActionItemDto,
  FormSubmissionTaskItemDto,
  useCreateFormSubmission,
  useEditFormSubmission,
  useGetForm,
  useGetFormSubmission,
  UserDto,
  type FormAnswerValueDto,
} from "src/http";
import { AddReferenceWorkModal, ReferencedWorkItemType, type ReferencedItemAddedParams } from "./AddReferenceWorkModal";
import { FormQuestionInput } from "./FormQuestionInput";

interface IFormValues {
  recipientUser: UserDto | null;
  dateRange: DateRange<DateTime>;
  videoUrl: string;
  answers: Record<string, FormAnswerValueDto | null>;
  referencedActionItems: FormSubmissionActionItemDto[];
  referencedTaskItems: FormSubmissionTaskItemDto[];
}

interface IProps {
  formGuid?: string;
  formSubmissionGuid?: string;
  visible: boolean;
  onCancelled: () => void;
  onSubmitted: () => void;
}

const quarterShortcuts: PickersShortcutsItem<DateRange<DateTime>>[] = [-2, -1, 0, 1].map((offset) => {
  const start = DateTime.now().startOf("quarter").plus({ quarters: offset });
  const end = start.endOf("quarter");
  const q = Math.ceil(start.month / 3);
  return {
    label: `Q${q} ${start.year}`,
    getValue: () => [start, end],
  };
});

const FormModal = (props: IProps) => {
  const [currentFormGuid, setCurrentFormGuid] = useState<string | undefined>(props.formGuid);
  const [lastFormGuid, setLastFormGuid] = useState<string | undefined>(props.formGuid);
  const [isReferenceWorkModalVisible, setIsReferenceWorkModalVisible] = useState(false);
  const renderTimeRef = useRef<DateTime>(DateTime.utc());
  const sessionUser = useSession();
  const { enqueueSnackbar } = useSnackbar();

  if (props.formGuid !== lastFormGuid) {
    setLastFormGuid(props.formGuid);
    if (props.formGuid !== undefined) {
      setCurrentFormGuid(props.formGuid);
    }
  }

  const { data: form, isLoading } = useGetForm(currentFormGuid);
  const { data: draftDetail, isLoading: isDraftLoading } = useGetFormSubmission(props.formSubmissionGuid ?? null);
  const { mutate: createFormSubmission, isLoading: isSubmitting } = useCreateFormSubmission();
  const { mutate: editFormSubmission, isLoading: isEditing } = useEditFormSubmission();

  const isSubmissionLoading = props.formSubmissionGuid != null && isDraftLoading;
  const isSaving = isSubmitting || isEditing;

  const defaultFormValues: IFormValues = {
    recipientUser: null,
    dateRange: [null, null],
    videoUrl: "",
    answers: {},
    referencedActionItems: [],
    referencedTaskItems: [],
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    trigger,
    getValues,
    setValue,
    reset,
  } = useForm<IFormValues>({
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    if (!draftDetail) return;
    reset({
      recipientUser: draftDetail.recipientUser,
      dateRange: [
        draftDetail.dateRangeStartUtc ? DateTime.fromISO(draftDetail.dateRangeStartUtc) : null,
        draftDetail.dateRangeEndUtc ? DateTime.fromISO(draftDetail.dateRangeEndUtc) : null,
      ],
      videoUrl: draftDetail.videoUrl ?? "",
      answers: Object.fromEntries(
        draftDetail.questionGroups.flatMap((g) => g.questions).map((q) => [q.formQuestionGuid, q.answer]),
      ),
      referencedActionItems: draftDetail.referencedActionItems.map((a) => ({
        actionItemGuid: a.actionItemGuid,
        description: a.description,
        status: a.status,
        team: a.team,
      })),
      referencedTaskItems: draftDetail.referencedTaskItems.map((t) => ({
        taskItemGuid: t.taskItemGuid,
        description: t.description,
        status: t.status,
        team: t.team,
      })),
    });
  }, [draftDetail, reset]);

  const referencedActionItems = useWatch({ control, name: "referencedActionItems" });
  const referencedTaskItems = useWatch({ control, name: "referencedTaskItems" });

  function handleReferenceItemAdded(params: ReferencedItemAddedParams) {
    switch (params.itemType) {
      case ReferencedWorkItemType.ActionItem: {
        if (
          params.actionItem != null &&
          !referencedActionItems.some((x) => x.actionItemGuid === params.actionItem?.actionItemGuid)
        ) {
          setValue("referencedActionItems", [...referencedActionItems, params.actionItem]);
        }
        break;
      }
      case ReferencedWorkItemType.TaskItem: {
        if (params.taskItem != null && !referencedTaskItems.some((x) => x.taskItemGuid === params.taskItem?.guid)) {
          setValue("referencedTaskItems", [
            ...referencedTaskItems,
            {
              taskItemGuid: params.taskItem.guid,
              description: params.taskItem.description,
              status: params.taskItem.currentStatus,
              team: params.taskItem.team ?? null,
            },
          ]);
        }
        break;
      }
    }

    setIsReferenceWorkModalVisible(false);
  }

  function saveSubmission(formState: IFormValues, submitted: boolean, successMessage: string) {
    if (!form || !sessionUser) return;

    const payload = {
      recipientUserId:
        form.recipientType === FormRecipientType.Self ? sessionUser.userId : formState.recipientUser!.userId,
      dateRangeStartUtc: formState.dateRange?.[0]?.toISODate() ?? null,
      dateRangeEndUtc: formState.dateRange?.[1]?.toISODate() ?? null,
      videoUrl: formState.videoUrl
        ? formState.videoUrl.startsWith("http")
          ? formState.videoUrl
          : `https://${formState.videoUrl}`
        : null,
      answers: Object.entries(formState.answers)
        .filter(([, value]) => value != null)
        .map(([questionGuid, value]) => ({ questionGuid, value })),
      referencedActionItemGuids: formState.referencedActionItems.map((a) => a.actionItemGuid),
      referencedTaskItemGuids: formState.referencedTaskItems.map((t) => t.taskItemGuid),
      submitted,
    };

    if (props.formSubmissionGuid) {
      editFormSubmission(
        { formSubmissionGuid: props.formSubmissionGuid, ...payload },
        {
          onSuccess: () => {
            reset(defaultFormValues, { keepDefaultValues: true });
            enqueueSnackbar(<SnackbarContent title="Success" content={successMessage} />, { variant: "success" });
            props.onSubmitted();
          },
        },
      );
    } else {
      if (!props.formGuid) return;
      createFormSubmission(
        { formGuid: props.formGuid, createdDateTimeUtc: renderTimeRef.current.toISO()!, ...payload },
        {
          onSuccess: () => {
            reset(defaultFormValues, { keepDefaultValues: true });
            enqueueSnackbar(<SnackbarContent title="Success" content={successMessage} />, { variant: "success" });
            props.onSubmitted();
          },
        },
      );
    }
  }

  async function handleSaveDraftClicked() {
    if (form?.recipientType !== FormRecipientType.Self) {
      const recipientValid = await trigger("recipientUser");
      if (!recipientValid) return;
    }
    saveSubmission(getValues(), false, `${form?.name} has been saved as draft.`);
  }

  function handleSubmitClicked() {
    handleSubmit((formState) => {
      saveSubmission(formState, true, `${form?.name} has been submitted.`);
    })();
  }

  function handleCancelClicked() {
    reset(defaultFormValues, { keepDefaultValues: true });
    props.onCancelled();
  }

  function handleDeleteActionItemClicked(actionItemGuid: string) {
    setValue(
      "referencedActionItems",
      referencedActionItems.filter((x) => x.actionItemGuid !== actionItemGuid),
    );
  }

  function handleDeleteTaskItemClicked(taskItemGuid: string) {
    setValue(
      "referencedTaskItems",
      referencedTaskItems.filter((x) => x.taskItemGuid !== taskItemGuid),
    );
  }

  const renderQuestion = (question: FormQuestionDto) => {
    if (question.typeConfig.type === "TextBlock") {
      return (
        <Stack key={question.guid} spacing={0.25}>
          {question.name && <Typography variant="body2">{question.name}</Typography>}
          <TextViewer value={question.typeConfig.content} />
        </Stack>
      );
    }

    return (
      <Controller
        key={question.guid}
        name={`answers.${question.guid}`}
        control={control}
        rules={{
          required: question.isRequired ? "This field is required" : false,
        }}
        render={({ field }) => (
          <Stack spacing={0.25}>
            {question.name && <SmallInputLabel>{`${question.name}${question.isRequired ? " *" : ""}`}</SmallInputLabel>}
            <Box>
              {errors.answers?.[question.guid] && (
                <Typography color="error" variant="caption">
                  {errors.answers[question.guid]?.message}
                </Typography>
              )}
              <FormQuestionInput
                question={question}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={isSubmissionLoading}
              />
            </Box>
          </Stack>
        )}
      />
    );
  };

  return (
    <>
      <Dialog open={props.visible} fullWidth>
        <DialogTitle>{isLoading ? <Skeleton width="40%" /> : (form?.name ?? "Form")}</DialogTitle>
        <DialogContent>
          {isLoading && (
            <Stack spacing={1}>
              <Stack spacing={0.25}>
                <Skeleton variant="text" width="90%" />
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="70%" />
              </Stack>
              <Skeleton variant="text" width="50%" />
            </Stack>
          )}
          {form && (
            <Stack spacing={1}>
              {form.description && <TextViewer value={form.description} />}

              <Stack direction="row" spacing={0.5}>
                <VisibilityOutlined sx={{ position: "relative", top: 3 }} />
                <Stack>
                  <Typography> The submitted form is by default visible to:</Typography>

                  {form.defaultPrivacySetting === FormSubmissionPrivacyType.Public && (
                    <Typography>&bull; All organization members</Typography>
                  )}

                  {form.defaultPrivacySetting === FormSubmissionPrivacyType.RecipientAndPeopleLead && (
                    <>
                      <Typography>
                        &bull; {form.recipientType === FormRecipientType.Self ? "Yourself" : "Recipient"}
                      </Typography>
                      <Typography>&bull; Current and future People Leads</Typography>
                      <Typography>&bull; Designated People and Culture members</Typography>
                    </>
                  )}

                  {form.defaultPrivacySetting === FormSubmissionPrivacyType.RecipientOnly && (
                    <>
                      <Typography>&bull; Recipient</Typography>
                      <Typography>&bull; Sender</Typography>
                    </>
                  )}

                  {form.recipientCanMakePublic && (
                    <Typography sx={{ mt: 0.25 }}>
                      Recipient can choose to make the feedback visible to all organization members
                    </Typography>
                  )}
                </Stack>
              </Stack>

              <Stack spacing={1}>
                {form.recipientType !== FormRecipientType.Self && (
                  <Stack spacing={0.25}>
                    <SmallInputLabel>Recipient *</SmallInputLabel>
                    <Controller
                      name="recipientUser"
                      control={control}
                      rules={{ required: "Recipient is required" }}
                      render={({ field }) => (
                        <>
                          {errors.recipientUser !== undefined && (
                            <Typography variant="caption" color="error">
                              {errors.recipientUser.message}
                            </Typography>
                          )}
                          <UserAutocomplete
                            value={field.value}
                            onChange={(user) => field.onChange(user)}
                            disabled={isSubmissionLoading}
                            optionsFilter={(user) =>
                              form.recipientType === FormRecipientType.DirectReportsForPeopleLead
                                ? user.peopleLeadUserId === sessionUser?.userId
                                : true
                            }
                          />
                        </>
                      )}
                    />
                  </Stack>
                )}

                {form.dateRangeRequired && (
                  <Stack spacing={0.25}>
                    <SmallInputLabel>{`${form.dateRangeLabel ?? "Date Range"} *`}</SmallInputLabel>
                    <Controller
                      control={control}
                      name="dateRange"
                      rules={{
                        validate: (value) =>
                          !form.dateRangeRequired ||
                          (value[0] != null && value[1] != null) ||
                          `${form.dateRangeLabel ?? "Date range"} is required`,
                      }}
                      render={({ field }) => (
                        <>
                          {errors.dateRange !== undefined && (
                            <Typography variant="caption" color="error">
                              {errors.dateRange.message}
                            </Typography>
                          )}

                          <LocalizationProvider dateAdapter={AdapterLuxon}>
                            <DateRangePicker
                              format="MM/dd/yyyy"
                              calendars={2}
                              value={field.value}
                              disabled={isSubmissionLoading}
                              onChange={(e) => {
                                field.onChange(e);
                              }}
                              slotProps={{
                                shortcuts: {
                                  items: form.formType === FormType.SelfReflection ? quarterShortcuts : [],
                                  sx: {
                                    "& .MuiListItem-root": {
                                      paddingTop: 0.25,
                                      paddingBottom: 0.25,
                                    },
                                    "& .MuiListItem-root:nth-of-type(3) .MuiChip-root": {
                                      backgroundColor: "primary.transparent10",
                                      "&:hover": {
                                        backgroundColor: "primary.transparent15",
                                      },
                                    },
                                  },
                                },
                                openPickerButton: { sx: { mr: 0.25 } },
                              }}
                            />
                          </LocalizationProvider>
                        </>
                      )}
                    />
                  </Stack>
                )}

                {form.videoUrlRequired && (
                  <Stack spacing={0.25}>
                    <SmallInputLabel>Link to Video</SmallInputLabel>
                    <Controller
                      control={control}
                      name="videoUrl"
                      render={({ field }) => (
                        <TextField
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          placeholder="Enter video url..."
                          disabled={isSubmissionLoading}
                          error={errors.videoUrl !== undefined}
                          helperText={errors.videoUrl?.message}
                        />
                      )}
                    />
                  </Stack>
                )}
              </Stack>

              <Stack spacing={1}>
                {[...form.questionGroups]
                  .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))
                  .map((group) => (
                    <Stack key={group.guid} spacing={0.5}>
                      <Box>
                        <Typography variant="body2">{group.name}</Typography>
                        <Divider />
                      </Box>

                      <Stack spacing={1}>
                        {[...group.questions]
                          .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))
                          .map(renderQuestion)}
                      </Stack>
                    </Stack>
                  ))}
              </Stack>

              <Stack spacing={1}>
                <Box>
                  <Typography variant="body2">Reference Work</Typography>
                  <Divider />
                </Box>

                {referencedActionItems.length > 0 && (
                  <Box>
                    <Stack direction="row" spacing={0.25} sx={{ alignItems: "center" }}>
                      <AssignmentTurnedInOutlined />
                      <Typography variant="label">Strategic Items</Typography>
                    </Stack>
                    <Stack>
                      {referencedActionItems
                        .sort((a, b) => a.description.localeCompare(b.description))
                        .map((actionItem) => (
                          <Stack key={actionItem.actionItemGuid} direction="row">
                            <ActionItemLabel
                              actionItemGuid={actionItem.actionItemGuid}
                              actionItemDescription={actionItem.description}
                              actionItemStatus={actionItem.status}
                              actionItemTeam={actionItem.team}
                              target="_blank"
                              sx={{ flexGrow: 1 }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteActionItemClicked(actionItem.actionItemGuid)}
                              sx={{ alignSelf: "start" }}
                            >
                              <DeleteRounded />
                            </IconButton>
                          </Stack>
                        ))}
                    </Stack>
                  </Box>
                )}

                {referencedTaskItems.length > 0 && (
                  <Box>
                    <Stack direction="row" spacing={0.25} sx={{ alignItems: "center" }}>
                      <CheckCircleOutlined />
                      <Typography variant="label">Tasks</Typography>
                    </Stack>
                    <Stack>
                      {referencedTaskItems
                        .sort((a, b) => a.description.localeCompare(b.description))
                        .map((taskItem) => (
                          <Stack key={taskItem.taskItemGuid} direction="row">
                            <TaskItemLabel
                              key={taskItem.taskItemGuid}
                              taskItemGuid={taskItem.taskItemGuid}
                              taskItemDescription={taskItem.description}
                              taskItemStatus={taskItem.status}
                              taskItemTeam={taskItem.team}
                              target="_blank"
                              sx={{ flexGrow: 1 }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteTaskItemClicked(taskItem.taskItemGuid)}
                              sx={{ alignSelf: "start" }}
                            >
                              <DeleteRounded />
                            </IconButton>
                          </Stack>
                        ))}
                    </Stack>
                  </Box>
                )}

                <Button
                  variant="tertiary"
                  onClick={() => setIsReferenceWorkModalVisible(true)}
                  sx={{ alignSelf: "start" }}
                >
                  Add Strategic Item or Task
                </Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <Divider />
        <DialogActions>
          <Stack direction="row" spacing={1} sx={{ justifyContent: "space-between", width: "100%" }}>
            <Stack direction="row" spacing={1} useFlexGap>
              <Button variant="outlined" onClick={handleCancelClicked} disabled={isSaving}>
                Cancel
              </Button>
              <Button variant="outlined" onClick={handleSaveDraftClicked} disabled={isLoading || isSaving}>
                Save as Draft
              </Button>
            </Stack>
            <Button variant="contained" onClick={handleSubmitClicked} disabled={isLoading || isSaving}>
              Submit
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <AddReferenceWorkModal
        open={isReferenceWorkModalVisible}
        onDone={(params) => handleReferenceItemAdded(params)}
        onClose={() => setIsReferenceWorkModalVisible(false)}
      />
    </>
  );
};

export { FormModal };
